import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiRefreshCw, FiAlertTriangle, FiClock, FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { listPendingApprovals, actionApproval, getClaim, errMsg } from '../../utils/expenseApi';

const ACTIONS = [
  { key: 'approve',  label: 'Approve',   cls: 'approve' },
  { key: 'reject',   label: 'Reject',    cls: 'reject' },
  { key: 'clarify',  label: 'Clarify',   cls: 'clarify' },
  { key: 'escalate', label: 'Escalate',  cls: 'escalate' },
];

const CATEGORY_LABELS = {
  TRAVEL_AIRFARE: 'Airfare', TRAVEL_HOTEL: 'Hotel',
  TRAVEL_GROUND: 'Ground Transport', TRAVEL_TRAIN: 'Train',
  MEALS: 'Meals', MEALS_CLIENT: 'Client Meals',
  OFFICE_SUPPLIES: 'Office Supplies', COMMUNICATION: 'Communication',
  CONFERENCE: 'Conference', FUEL: 'Fuel',
  VISA_PASSPORT: 'Visa / Passport', MEDICAL: 'Medical',
  MISCELLANEOUS: 'Miscellaneous',
};

function ActionModal({ approval, onDone, onClose }) {
  const [action,   setAction]   = useState('approve');
  const [comments, setComments] = useState('');
  const [loading,  setLoading]  = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (action === 'reject' && !comments.trim()) {
      toast.error('A comment is required when rejecting.'); return;
    }
    setLoading(true);
    try {
      await actionApproval(approval.approval_id, action, comments);
      const verb = action === 'approve' ? 'Approved' : action === 'reject' ? 'Rejected' : action === 'clarify' ? 'Clarification requested' : 'Escalated';
      toast.success(`${verb} successfully.`);
      onDone();
    } catch (err) {
      toast.error(errMsg(err, 'Action failed. Please try again.'));
    }
    setLoading(false);
  };

  return (
    <div className="exp-modal-overlay" onClick={onClose}>
      <motion.div
        className="exp-modal"
        initial={{ opacity: 0, scale: 0.95, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 16 }}
        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h3 className="exp-modal-title" style={{ margin: 0 }}>
            Action Approval #{approval.approval_id}
          </h3>
          <button className="exp-icon-btn" onClick={onClose}><FiX size={14} /></button>
        </div>

        <div className="exp-detail-grid" style={{ marginBottom: 20 }}>
          <div className="exp-detail-item">
            <span className="exp-detail-lbl">Amount</span>
            <span className="exp-detail-val" style={{ color: 'var(--ep)', fontWeight: 800 }}>
              {parseFloat(approval.claim_total_amount).toLocaleString('en-IN')} {approval.claim_currency}
            </span>
          </div>
          <div className="exp-detail-item">
            <span className="exp-detail-lbl">Tier</span>
            <span className="exp-detail-val">{approval.approval_tier} — {approval.approver_role}</span>
          </div>
          <div className="exp-detail-item">
            <span className="exp-detail-lbl">SLA Deadline</span>
            <span className="exp-detail-val" style={{ color: approval.sla_breached ? 'var(--e-err)' : 'inherit' }}>
              {new Date(approval.sla_deadline).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
              {approval.sla_breached ? ' ⚠ Breached' : ''}
            </span>
          </div>
          <div className="exp-detail-item">
            <span className="exp-detail-lbl">Claim ID</span>
            <span className="exp-detail-val" style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
              {approval.claim_id?.slice(0, 16)}…
            </span>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="exp-field" style={{ marginBottom: 14 }}>
            <label className="exp-label">Action *</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {ACTIONS.map(a => (
                <motion.button
                  key={a.key}
                  type="button"
                  className={`exp-btn small ${action === a.key ? a.cls : ''}`}
                  onClick={() => setAction(a.key)}
                  whileTap={{ scale: 0.96 }}
                  style={action !== a.key ? { opacity: 0.6 } : {}}
                >
                  {a.label}
                </motion.button>
              ))}
            </div>
          </div>

          <div className="exp-field" style={{ marginBottom: 16 }}>
            <label className="exp-label">
              Comments {action === 'reject' ? '(required)' : '(optional)'}
            </label>
            <textarea
              className="exp-textarea"
              rows={3}
              value={comments}
              onChange={e => setComments(e.target.value)}
              maxLength={2000}
              placeholder={
                action === 'approve'  ? 'Optional: add a note for the claimant…' :
                action === 'reject'   ? 'Reason for rejection…' :
                action === 'clarify'  ? 'What additional information do you need?' :
                'Escalation reason…'
              }
            />
          </div>

          <div className="exp-modal-footer">
            <motion.button type="button" className="exp-btn ghost" onClick={onClose} whileTap={{ scale: 0.96 }}>
              Cancel
            </motion.button>
            <motion.button
              type="submit"
              className={`exp-btn ${ACTIONS.find(a => a.key === action)?.cls || 'primary'}`}
              disabled={loading}
              whileTap={{ scale: 0.96 }}
            >
              {loading
                ? <><div className="exp-inline-spinner" /> Processing…</>
                : `Confirm ${ACTIONS.find(a => a.key === action)?.label}`
              }
            </motion.button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

function ApprovalCard({ approval, onAction }) {
  const [claimDetail, setClaimDetail] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const loadDetail = async () => {
    if (claimDetail) { setExpanded(e => !e); return; }
    try {
      const d = await getClaim(approval.claim_id);
      setClaimDetail(d);
      setExpanded(true);
    } catch (_) {
      setExpanded(e => !e);
    }
  };

  const timeLeft = approval.sla_deadline
    ? Math.round((new Date(approval.sla_deadline) - new Date()) / 3600000)
    : null;

  return (
    <div className={`exp-approval-card ${approval.sla_breached ? 'sla-breached' : ''}`}>
      <div className="exp-approval-top">
        <div>
          <div style={{ fontWeight: 800, fontSize: '1.1rem', color: 'var(--ep)', fontFamily: "'Space Grotesk',sans-serif" }}>
            {parseFloat(approval.claim_total_amount).toLocaleString('en-IN')} {approval.claim_currency}
          </div>
          <div style={{ fontSize: '0.8rem', color: 'var(--e-muted)', marginTop: 2 }}>
            Tier {approval.approval_tier} · {approval.approver_role}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
          {approval.sla_breached ? (
            <span className="exp-badge sla-breach"><FiAlertTriangle size={10} /> SLA Breached</span>
          ) : timeLeft !== null ? (
            <span className="exp-badge sla-ok"><FiClock size={10} /> {timeLeft > 0 ? `${timeLeft}h left` : 'Due now'}</span>
          ) : null}
        </div>
      </div>

      <div className="exp-approval-meta">
        <span>Assigned: <strong>{new Date(approval.assigned_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</strong></span>
        <span>Deadline: <strong style={{ color: approval.sla_breached ? 'var(--e-err)' : 'inherit' }}>
          {new Date(approval.sla_deadline).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
        </strong></span>
        <span>Claim: <strong style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{approval.claim_id?.slice(0, 16)}…</strong></span>
      </div>

      {/* Expanded claim detail */}
      <AnimatePresence>
        {expanded && claimDetail && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.22 }}
            style={{ overflow: 'hidden' }}
          >
            <div className="exp-detail-panel" style={{ marginTop: 0 }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--e-muted)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 10 }}>
                Claim Lines ({claimDetail.lines?.length || 0})
              </div>
              <div className="exp-table-wrap">
                <table className="exp-table" style={{ fontSize: '0.82rem' }}>
                  <thead>
                    <tr><th>Category</th><th>Date</th><th>Vendor</th><th>Amount</th><th>Policy Exc?</th></tr>
                  </thead>
                  <tbody>
                    {(claimDetail.lines || []).map(l => (
                      <tr key={l.line_id}>
                        <td>{CATEGORY_LABELS[l.expense_category] || l.expense_category}</td>
                        <td>{l.expense_date}</td>
                        <td>{l.vendor_name || '—'}</td>
                        <td style={{ fontWeight: 700 }}>{parseFloat(l.amount).toLocaleString('en-IN')}</td>
                        <td>{l.is_policy_exception ? <span className="exp-badge PENDING_APPROVAL">Yes</span> : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {claimDetail.description && (
                <div style={{ marginTop: 10, fontSize: '0.84rem', color: 'var(--e-muted)' }}>
                  <strong>Description:</strong> {claimDetail.description}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <motion.button
          className="exp-btn ghost xs"
          onClick={loadDetail}
          whileTap={{ scale: 0.96 }}
        >
          {expanded ? '▲ Hide detail' : '▼ View claim'}
        </motion.button>
        <div className="exp-approval-actions">
          {ACTIONS.map(a => (
            <motion.button
              key={a.key}
              className={`exp-btn xs ${a.cls}`}
              onClick={() => onAction(approval)}
              whileTap={{ scale: 0.96 }}
            >
              {a.label}
            </motion.button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ExpenseApprovals() {
  const [approvals, setApprovals] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [tierFilter, setTierFilter] = useState('');
  const [modal,     setModal]     = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listPendingApprovals(tierFilter || undefined);
      setApprovals(Array.isArray(data) ? data : []);
    } catch (_) {}
    setLoading(false);
  };

  useEffect(() => { load(); }, [tierFilter]);

  const handleActionDone = () => {
    setModal(null);
    load();
  };

  const breached = approvals.filter(a => a.sla_breached).length;

  return (
    <motion.div className="exp-section" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <h2 className="exp-section-title" style={{ margin: 0 }}>Pending Approvals</h2>
          {breached > 0 && (
            <div className="exp-alert warn" style={{ marginTop: 8, marginBottom: 0, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <FiAlertTriangle size={14} />
              {breached} approval{breached > 1 ? 's' : ''} past SLA deadline
            </div>
          )}
        </div>
        <motion.button className="exp-btn ghost small" onClick={load} whileTap={{ scale: 0.95 }}>
          <FiRefreshCw size={12} /> Refresh
        </motion.button>
      </div>

      {/* Tier filter */}
      <div className="exp-filter-bar">
        <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--e-muted)' }}>Tier:</span>
        {['', '1', '2', '3'].map(t => (
          <motion.button
            key={t || 'all'}
            className={`exp-btn xs ${tierFilter === t ? 'primary' : ''}`}
            onClick={() => setTierFilter(t)}
            whileTap={{ scale: 0.96 }}
          >
            {t ? `Tier ${t}` : 'All'}
          </motion.button>
        ))}
      </div>

      {loading ? (
        <div className="exp-spinner-wrap"><div className="exp-spinner" /></div>
      ) : approvals.length === 0 ? (
        <div className="exp-empty">
          <div className="exp-empty-icon">✅</div>
          <div className="exp-empty-msg">All caught up!</div>
          <div className="exp-empty-sub">No pending approvals{tierFilter ? ` for Tier ${tierFilter}` : ''}.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {approvals.map(a => (
            <ApprovalCard
              key={a.approval_id}
              approval={a}
              onAction={setModal}
            />
          ))}
        </div>
      )}

      <AnimatePresence>
        {modal && (
          <ActionModal
            approval={modal}
            onDone={handleActionDone}
            onClose={() => setModal(null)}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}
